import { Star } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { JourneyPortfolio } from './studyYearApi';
import { MAX_STORY_HIGHLIGHTS, MAX_STORY_NARRATIVE, type JourneyStory } from './useJourneyStory';

/** The star on a My journey record (`Add to my year story`, at most 20). */
export function StoryStar({
  story,
  recordKey,
  title,
}: {
  story: JourneyStory;
  recordKey: string;
  title: string;
}) {
  const on = story.starred.has(recordKey);
  const full = !on && story.starred.size >= MAX_STORY_HIGHLIGHTS;
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={`Add to my year story: ${title}`}
      title={full ? 'You can add up to 20 highlights.' : 'Add to my year story'}
      disabled={!story.query.data || story.save.isPending || full}
      onClick={() => story.toggleStar(recordKey)}
      className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-hairline bg-canvas-pure text-ink disabled:opacity-50"
    >
      <Star
        className={on ? 'h-5 w-5 fill-brand-sunshine text-ink' : 'h-5 w-5 text-slate2'}
        aria-hidden="true"
      />
    </button>
  );
}

function StoryEditor({
  saved,
  story,
  onSaved,
}: {
  saved: JourneyPortfolio;
  story: JourneyStory;
  onSaved?: () => void;
}) {
  const [narrative, setNarrative] = useState(saved.narrative);
  const tooLong = narrative.length > MAX_STORY_NARRATIVE;
  return (
    <form
      aria-label="My year story"
      className="mt-5 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (tooLong) return;
        story.save.mutate(
          { narrative: narrative.trim(), highlights: [...story.starred] },
          { onSuccess: () => onSaved?.() },
        );
      }}
    >
      <label htmlFor="journey-story-text" className="block text-sm font-bold text-ink">
        In my own words
      </label>
      <textarea
        id="journey-story-text"
        rows={5}
        maxLength={MAX_STORY_NARRATIVE}
        value={narrative}
        onChange={(event) => setNarrative(event.target.value)}
        className="w-full rounded-2xl border border-hairline bg-canvas-pure p-3 text-sm text-ink"
      />
      <p className="text-xs font-semibold text-slate2">
        {narrative.length}/{MAX_STORY_NARRATIVE}
      </p>
      {story.save.isError && (
        <p role="alert" className="text-sm text-ink">
          This part didn&apos;t save. Your words are still here.{' '}
          <button
            type="button"
            className="font-bold underline"
            onClick={() => void story.query.refetch()}
          >
            Load the latest story
          </button>
        </p>
      )}
      {story.save.isSuccess && (
        <p role="status" className="text-sm font-bold text-ink">
          Your story is saved.
        </p>
      )}
      <button type="submit" disabled={story.save.isPending} className="btn-pill-secondary">
        {story.save.isPending ? 'Saving…' : 'Save my story'}
      </button>
    </form>
  );
}

/** "My year story" at the end of My journey, collapsed unless opened (PRD §6.1 item 5). */
export function StudyYearStory({
  story,
  yearId,
  defaultOpen,
  onSaved,
}: {
  story: JourneyStory;
  yearId: string;
  defaultOpen: boolean;
  onSaved?: () => void;
}) {
  const { query } = story;
  return (
    <details
      id="story"
      open={defaultOpen || undefined}
      className="mt-8 rounded-3xl border border-hairline bg-canvas-pure p-5 sm:p-6"
    >
      <summary className="min-h-11 cursor-pointer content-center text-xl font-extrabold text-ink">
        My year story
      </summary>
      <p className="mt-3 text-sm text-ink-soft">
        Only you can see this unless you choose to share it.
      </p>
      {query.isLoading && <p role="status">Loading your story…</p>}
      {query.isError && (
        <div role="alert" className="mt-3">
          <p>This part didn&apos;t load. Your work is still saved.</p>
          <button
            type="button"
            className="btn-pill-ghost mt-3"
            onClick={() => void query.refetch()}
          >
            Try again
          </button>
        </div>
      )}
      {query.data && (
        <>
          <h3 className="mt-5 text-sm font-extrabold text-ink">
            Highlights {story.starred.size}/{MAX_STORY_HIGHLIGHTS}
          </h3>
          {query.data.highlights.length === 0 ? (
            <p className="mt-2 text-sm text-slate2">
              Star a record above to add it to your year story.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {query.data.highlights.map((highlight) => (
                <li key={highlight.key} className="text-sm">
                  {highlight.available && highlight.href ? (
                    <Link className="font-bold text-ink underline" to={highlight.href}>
                      {highlight.title}
                    </Link>
                  ) : (
                    <span className="text-slate2">
                      {highlight.title} · This item was deleted
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <StoryEditor key={yearId} saved={query.data} story={story} onSaved={onSaved} />
        </>
      )}
    </details>
  );
}
